<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import GaiaSegmented, { type GaiaSegmentedOption } from '../../../base/GaiaSegmented.vue';
import GaiaText from '../../../base/GaiaText.vue';
import GaiaVaultPicker from '../../fileVault/GaiaVaultPicker.vue';
import { emptyVaultPickerCriteria } from '../../fileVault/vaultPicker';
import type {
  CalendarContractCriteria,
  CalendarSourceMode,
  CalendarWizardForm,
} from '@/composables/useCalendarWizard';

/**
 * Pick what this calendar can see — never more than its creator can read.
 * Either folders and contracts picked from the Contract Vault, or every
 * contract matching a search; only the chosen one is saved. Switching keeps
 * the other's state in the form until then, so nothing is lost by looking.
 */
const props = defineProps({
  form: {
    type: Object as PropType<CalendarWizardForm>,
    required: true,
  },
});

const emit = defineEmits<{ 'update:form': [form: CalendarWizardForm] }>();

function patch(changes: Partial<CalendarWizardForm>): void {
  emit('update:form', { ...props.form, ...changes });
}

/** The picker remounts per mode; its count arrives once the new one has searched. */
function switchMode(mode: CalendarSourceMode): void {
  matchCount.value = null;
  patch({ sourceMode: mode });
}

const modeOptions: GaiaSegmentedOption[] = [
  { label: 'Pick folders & contracts', value: 'pick' },
  { label: 'Contracts matching a search', value: 'search' },
];

/** In pick mode the search only helps find things to tick, so it is never saved. */
const findCriteria = ref<CalendarContractCriteria>(emptyVaultPickerCriteria());

/** How many contracts the saved search matches now, once the table has counted. */
const matchCount = ref<number | null>(null);

const pickedParts = computed(() =>
  (
    [
      [props.form.sourceDirectoryUids.length, 'folder'],
      [props.form.sourceContractUids.length, 'contract'],
    ] as const
  )
    .filter(([count]) => count > 0)
    .map(([count, noun]) => `${count} ${noun}${count === 1 ? '' : 's'}`)
);

const summary = computed(() => {
  if (props.form.sourceMode === 'search') {
    if (matchCount.value === null) return '';
    const noun = matchCount.value === 1 ? 'contract matches' : 'contracts match';
    return `${matchCount.value} ${noun} now — new matches are added automatically.`;
  }
  return pickedParts.value.length > 0
    ? `Selected: ${pickedParts.value.join(' · ')}`
    : 'Nothing selected';
});
</script>

<template>
  <n-flex vertical :size="10" :wrap="false">
    <gaia-segmented
      :value="props.form.sourceMode"
      :options="modeOptions"
      @update:value="value => switchMode(value as CalendarSourceMode)"
    />
    <gaia-text size="meta" tone="subtle">
      {{
        props.form.sourceMode === 'pick'
          ? 'A folder includes everything in it and its subfolders, including files uploaded later. A contract is included on its own.'
          : 'Every contract matching the search is included — now, and whenever one is added or changed. With no search or filter, that is every contract you can read.'
      }}
    </gaia-text>
    <!-- Keyed by mode: each mode starts on its own search, page and folder. -->
    <gaia-vault-picker
      :key="props.form.sourceMode"
      :scope="props.form.sourceMode === 'pick' ? 'vault' : 'contracts'"
      :selectable="props.form.sourceMode === 'pick' ? ['directory', 'contract'] : []"
      :criteria="props.form.sourceMode === 'pick' ? findCriteria : props.form.contractCriteria"
      :directory-uids="props.form.sourceDirectoryUids"
      :contract-uids="props.form.sourceContractUids"
      @update:criteria="
        (criteria: CalendarContractCriteria) =>
          props.form.sourceMode === 'pick'
            ? (findCriteria = criteria)
            : patch({ contractCriteria: criteria })
      "
      @update:directory-uids="(uids: string[]) => patch({ sourceDirectoryUids: uids })"
      @update:contract-uids="(uids: string[]) => patch({ sourceContractUids: uids })"
      @count="(count: number) => (matchCount = count)"
    />
    <gaia-text v-if="summary" size="meta" tone="subtle">{{ summary }}</gaia-text>
    <!-- Only reachable through the API: a calendar with picks and a search. -->
    <gaia-text
      v-if="props.form.sourceMode === 'search' && pickedParts.length > 0"
      size="meta"
      tone="subtle"
    >
      Saving removes what this calendar also has picked: {{ pickedParts.join(' · ') }}.
    </gaia-text>
  </n-flex>
</template>
